import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { notificationsApi, productsApi } from '../../services/apiService';
import * as messageService from '../../services/messageService';
import { API_BASE_URL } from '../../config/api';
import './MobileMessengerPage.css';

const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  if (avatar.startsWith('/uploads')) return avatar;
  return `${API_BASE_URL}${avatar}`;
};

const MobileMessengerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { on, off } = useSocket();
  const [promoBannerOpen, setPromoBannerOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [notifCount, setNotifCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [convError, setConvError] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null); // { convId, x, y }
  const contextRef = useRef(null);
  const longPressTimer = useRef(null);
  const longPressActivated = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showMenu]);

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handler = (e) => {
      if (contextRef.current && !contextRef.current.contains(e.target)) setContextMenu(null);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [contextMenu]);

  const handleLongPressStart = (convId, e) => {
    const touch = e.touches?.[0] || e;
    const x = touch.clientX;
    const y = touch.clientY;
    longPressTimer.current = setTimeout(() => {
      longPressActivated.current = true;
      setContextMenu({ convId, x, y });
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };

  const handleArchive = async (convId) => {
    setContextMenu(null);
    try {
      await messageService.archiveConversation(convId, true);
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch (_) {}
  };

  const handleDelete = async (convId) => {
    setContextMenu(null);
    try {
      await messageService.deleteConversation(convId, user?.id);
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch (_) {}
  };

  const menuItems = [
    { label: 'Start a new group chat', icon: 'group', action: () => navigate('/mobile/group-chat') },
    { label: 'Cloud drive', icon: 'drive', action: () => navigate('/mobile/cloud') },
    { label: 'Archive', icon: 'archive', action: () => navigate('/mobile/archive') },
    { label: 'Contacts', icon: 'contacts', action: () => navigate('/mobile/contacts') },
    { label: 'App feedback', icon: 'feedback', action: () => navigate('/mobile/feedback') },
  ];

  // ─── Fetch real conversations (same API as desktop) ───────────────────
  const fetchConversations = useCallback(async (silent = false) => {
    const uid = user?.id != null ? Number(user.id) : NaN;
    if (!user?.id || Number.isNaN(uid)) {
      setLoadingConversations(false);
      setConvError('Missing account id. Please sign out and sign in again.');
      return;
    }
    try {
      if (!silent) setLoadingConversations(true);
      setConvError(null);
      const data = await messageService.getConversations(uid);
      console.log('[Messenger] conversations fetched for user', user.id, ':', data);
      setConversations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[Messenger] fetchConversations error:', err);
      setConvError(err?.message || 'Failed to load messages');
    } finally {
      setLoadingConversations(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Polling every 8s (same interval as desktop)
  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => fetchConversations(true), 8000);
    return () => clearInterval(interval);
  }, [user?.id, fetchConversations]);

  useEffect(() => {
    if (!on || !off || !user?.id) return;
    const handleConversationUpdate = () => fetchConversations(true);
    on('conversation_updated', handleConversationUpdate);
    return () => off('conversation_updated', handleConversationUpdate);
  }, [on, off, user?.id, fetchConversations]);

  // ─── Fetch notifications count + featured products ─────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const notifData = await notificationsApi.getMy();
        const unread = (notifData.notifications || []).filter(n => !n.isRead).length;
        setNotifCount(unread);
      } catch (_) {}

      try {
        setLoadingProducts(true);
        const data = await productsApi.getFeatured();
        const items = data.products || data || [];
        setRecommendedProducts(items.slice(0, 6));
      } catch (_) {
        setRecommendedProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchData();
  }, []);

  // ─── Filter conversations ──────────────────────────────────────────────
  const filteredConversations = conversations.filter(c => {
    const name = c.sender?.name || '';
    const preview = c.preview || '';
    const matchesSearch = !searchQuery ||
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      preview.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (activeFilter === 'unread') return !c.isRead;
    return true;
  });

  const productCategories = ['All', 'Sports & Entertainment', 'Consumer Electronics', 'Home & Garden', 'Construction'];
  const [activeCategory, setActiveCategory] = useState('All');

  const visibleProducts = activeCategory === 'All'
    ? recommendedProducts
    : recommendedProducts.filter(p => p.category === activeCategory);

  return (
    <>
    <div className="mm-page">
      {/* Header */}
      <div className="mm-header">
        <h1 className="mm-title">Messenger</h1>
        <div className="mm-header-actions">
          <div className="mm-menu-wrap" ref={menuRef}>
            <button
              className="mm-icon-btn"
              aria-label="More"
              onClick={() => setShowMenu(v => !v)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>

            {showMenu && (
              <div className="mm-dropdown">
                {menuItems.map(item => (
                  <button
                    key={item.icon}
                    className="mm-dropdown-option"
                    type="button"
                    onClick={() => { setShowMenu(false); item.action(); }}
                  >
                    <span className="mm-dropdown-icon">
                      {item.icon === 'group' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <rect x="2" y="7" width="7" height="14" rx="1"/>
                          <rect x="9" y="3" width="13" height="18" rx="1"/>
                          <line x1="12" y1="7" x2="12" y2="21"/>
                          <line x1="16" y1="7" x2="16" y2="21"/>
                        </svg>
                      )}
                      {item.icon === 'drive' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <rect x="2" y="3" width="20" height="14" rx="2"/>
                          <path d="M8 21h8M12 17v4"/>
                        </svg>
                      )}
                      {item.icon === 'archive' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <rect x="2" y="3" width="20" height="5" rx="1"/>
                          <path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8"/>
                          <path d="M10 12h4"/>
                        </svg>
                      )}
                      {item.icon === 'contacts' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                        </svg>
                      )}
                      {item.icon === 'feedback' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      )}
                    </span>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mm-scroll-area">
        {/* Promo Banner */}
        {promoBannerOpen && (
          <div className="mm-promo-banner">
            <div className="mm-promo-icon">🚀</div>
            <div className="mm-promo-text">
              <strong>Upgrade: 10-year chat history</strong>
              <p>New messages are stored for 10 years now! (Message retention policies apply)</p>
            </div>
            <button className="mm-promo-close" onClick={() => setPromoBannerOpen(false)} aria-label="Close">✕</button>
          </div>
        )}

        {/* Quick Access Cards */}
        <div className="mm-quick-cards">
          <button className="mm-quick-card" onClick={() => navigate('/mobile/orders')}>
            <div className="mm-quick-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 3v18" />
                <path d="M13 7h4M13 12h4M13 17h4" />
              </svg>
            </div>
            <span>Orders</span>
          </button>

          <button className="mm-quick-card" onClick={() => navigate('/mobile/notifications')}>
            <div className="mm-quick-icon mm-quick-icon--bell">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              {notifCount > 0 && <span className="mm-badge">{notifCount > 9 ? '9+' : notifCount}</span>}
            </div>
            <span>Notifications</span>
          </button>

          <button className="mm-quick-card" onClick={() => navigate('/mobile/other-messages')}>
            <div className="mm-quick-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
              </svg>
            </div>
            <span>Others</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="mm-search-wrap">
          <div className="mm-search-bar">
            <svg className="mm-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="mm-search-input"
              type="text"
              placeholder="Search messages or suppliers"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Pills */}
        <div className="mm-filters">
          <button
            className={`mm-filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >All</button>
          <button
            className={`mm-filter-pill ${activeFilter === 'unread' ? 'active' : ''}`}
            onClick={() => setActiveFilter('unread')}
          >Unread</button>
          <button className="mm-archive-btn" aria-label="Archive" onClick={() => navigate('/mobile/archive')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="5" rx="1" />
              <path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8" />
              <path d="M10 12h4" />
            </svg>
          </button>
        </div>

        {/* Conversations / Loading / Empty State */}
        {loadingConversations ? (
          <div className="mm-conv-loading">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="mm-conv-skeleton">
                <div className="mm-skel-avatar" />
                <div className="mm-skel-body">
                  <div className="mm-skel-line mm-skel-name" />
                  <div className="mm-skel-line mm-skel-preview" />
                </div>
              </div>
            ))}
          </div>
        ) : convError ? (
          <div className="mm-empty-state">
            <div className="mm-empty-icon">
              <svg viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="30" fill="#fff0f0" />
                <path d="M40 26v16M40 50v2" stroke="#FF3B30" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="mm-empty-text" style={{ color: '#FF3B30' }}>Could not load messages</p>
            <p style={{ fontSize: 12, color: '#999', margin: '4px 0 12px', textAlign: 'center' }}>{convError}</p>
            <button
              onClick={() => fetchConversations()}
              style={{ padding: '8px 20px', background: '#FF6B00', color: '#fff', border: 'none', borderRadius: 20, fontSize: 13, cursor: 'pointer' }}
            >Retry</button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="mm-empty-state">
            <div className="mm-empty-icon">
              <svg viewBox="0 0 80 80" fill="none">
                <rect x="15" y="20" width="50" height="40" rx="4" fill="#e8e8e8" />
                <path d="M25 35h30M25 45h20" stroke="#bbb" strokeWidth="3" strokeLinecap="round" />
                <path d="M40 60l5 8h-10l5-8z" fill="#e8e8e8" />
              </svg>
            </div>
            <p className="mm-empty-text">
              {activeFilter === 'unread' && conversations.length > 0
                ? 'No unread messages'
                : 'No messages yet'}
            </p>
          </div>
        ) : (
          <div className="mm-conversations">
            {filteredConversations.map(conv => {
              const name = conv.sender?.name || 'Unknown';
              const avatarUrl = getAvatarUrl(conv.sender?.avatar);
              const initial = name.charAt(0).toUpperCase();
              return (
                <button
                  key={conv.id}
                  className={`mm-conv-item ${!conv.isRead ? 'mm-conv-unread' : ''}`}
                  onClick={() => {
                    if (longPressActivated.current) { longPressActivated.current = false; return; }
                    navigate(`/mobile/chat/${conv.id}`);
                  }}
                  onMouseDown={e => handleLongPressStart(conv.id, e)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={e => { e.currentTarget.style.userSelect = 'none'; handleLongPressStart(conv.id, e); }}
                  onTouchEnd={handleLongPressEnd}
                  onTouchCancel={handleLongPressEnd}
                  type="button"
                >
                  <div className="mm-conv-avatar">
                    {avatarUrl
                      ? <img src={avatarUrl} alt={name} />
                      : <span>{initial}</span>
                    }
                  </div>
                  <div className="mm-conv-info">
                    <div className="mm-conv-name">{name}</div>
                    <div className="mm-conv-last">{conv.preview || 'No messages yet'}</div>
                  </div>
                  <div className="mm-conv-meta">
                    <span className="mm-conv-time">{conv.time || ''}</span>
                    {!conv.isRead && <span className="mm-conv-badge" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Order Protection Notice */}
        <div className="mm-notice">
          <svg viewBox="0 0 24 24" fill="none" stroke="#ff6a00" strokeWidth="2" className="mm-notice-icon">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
          <p>Keep chats and transactions on GulfConstructor.com to enjoy order protection. <a href="/terms">Learn more</a></p>
        </div>

        {/* Recommended For You */}
        <div className="mm-recommended">
          <h2 className="mm-rec-title">Recommended for you</h2>
          <div className="mm-rec-tabs">
            <button className="mm-rec-search-btn" aria-label="Search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
            <div className="mm-rec-cats">
              {productCategories.map(cat => (
                <button
                  key={cat}
                  className={`mm-rec-cat ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >{cat}</button>
              ))}
            </div>
            <button className="mm-rec-more-btn" aria-label="More categories">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>

          {loadingProducts ? (
            <div className="mm-rec-loading">
              {[1, 2, 3, 4].map(i => <div key={i} className="mm-rec-skeleton" />)}
            </div>
          ) : (
            <div className="mm-rec-grid">
              {visibleProducts.map((product) => (
                <div
                  key={product.id}
                  className="mm-rec-card"
                  onClick={() => navigate(`/mobile/product/${product.id}`)}
                >
                  <div className="mm-rec-img">
                    {product.image || product.images?.[0] ? (
                      <img src={product.image || product.images[0]} alt={product.name} />
                    ) : (
                      <div className="mm-rec-img-placeholder">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="mm-rec-info">
                    <p className="mm-rec-name">{product.name || product.title || 'Product'}</p>
                    {product.price && (
                      <p className="mm-rec-price">
                        ${typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Long-press context menu */}
      {contextMenu && (
        <div
          ref={contextRef}
          className="mm-context-menu"
          style={{
            top: Math.min(contextMenu.y, window.innerHeight - 130),
            left: Math.min(contextMenu.x, window.innerWidth - 180)
          }}
        >
          <button
            className="mm-context-item"
            type="button"
            onClick={() => { setContextMenu(null); navigate(`/mobile/chat/${contextMenu.convId}`); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            Open chat
          </button>
          <button
            className="mm-context-item mm-context-archive"
            type="button"
            onClick={() => handleArchive(contextMenu.convId)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="2" y="3" width="20" height="5" rx="1" />
              <path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8" />
              <path d="M10 12h4" />
            </svg>
            Archive chat
          </button>
          <button
            className="mm-context-item mm-context-delete"
            type="button"
            onClick={() => handleDelete(contextMenu.convId)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
            </svg>
            Delete chat
          </button>
        </div>
      )}
    </>
  );
};

export default MobileMessengerPage;
